"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, CircleCheck as CheckCircle2, ShieldCheck, ArrowRight, CircleQuestionMark as HelpCircle, TrendingUp, FileCheck2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const tierList = [{
  id: "powder",
  name: "Powder Room Refresh",
  subtitle: "Half-bath design updates and statement powder room transformations",
  investment: "$18,000 – $30,000",
  timeline: "2 – 3 Weeks",
  popular: false,
  description: "Designed for main-level half bathrooms and historic under-stair powder rooms where space efficiency, architectural character, and high-impact finishes matter most without relocating major drain stacks.",
  scope1: "Solid-wood furniture vanity with quartz/marble top and undermount porcelain basin",
  scope2: "Designer hand-blocked wallpaper or custom tongue-and-groove beadboard wainscoting",
  scope3: "Solid unlacquered brass or matte black plumbing fixtures with ceramic disc cartridges",
  scope4: "Dedicated 20-amp GFCI circuit update with recessed ceiling and dual decorative sconce lighting",
  scope5: "City of Decatur or DeKalb County trade permitting, rough-in inspections, and final mechanical sign-offs"
}, {
  id: "guest",
  name: "Hall & Guest Bathroom",
  subtitle: "Full-bath spatial reconfiguration, waterproofing systems, and surface overhauls",
  investment: "$35,000 – $60,000",
  timeline: "4 – 6 Weeks",
  popular: true,
  description: "A complete down-to-the-studs overhaul tailored to busy family hallways and guest suites, combining durable waterproof assemblies with timeless period craftsmanship.",
  scope1: "Custom tile tub surround or walk-in shower conversion with Schluter-KERDI waterproof envelope",
  scope2: "Subfloor structural truing and joist sistering to support heavy porcelain, cast iron, or natural stone",
  scope3: "Custom 48\" to 60\" single or dual vanity with premium quartz countertop and soft-close dovetail joinery",
  scope4: "Complete replacement of old galvanized or copper lines with dedicated PEX-a home-run plumbing manifolds",
  scope5: "Quiet 110 CFM Panasonic WhisperValue ceiling exhaust fan vented directly through the exterior roofline"
}, {
  id: "primary",
  name: "Primary Suite & Wet Room",
  subtitle: "Custom architectural spa environments, curbless walk-ins, and expanded owner suites",
  investment: "$70,000 – $120,000+",
  timeline: "7 – 10 Weeks",
  popular: false,
  description: "Engineered luxury for master suites requiring architectural floor plan expansions, wall removals, structural beam installations, and bespoke wellness fixtures.",
  scope1: "Zero-threshold curbless shower suite with linear drain and custom 1/2\" ultra-clear tempered glass",
  scope2: "Freestanding stone-composite or enameled cast-iron soaking tub paired with floor-mount brass filler",
  scope3: "Dual bespoke white-oak or painted vanity suites, integrated linen cabinetry, and medicine niches",
  scope4: "Programmable hydronic or electric radiant floor heating system beneath honed marble or artisan zellige tile",
  scope5: "Thermostatic rough-in valves powering dual rain showers, handshowers, and independent volume controls"
}];
const faqs = [{
  id: "faq-1",
  question: "How do older Decatur home structures (craftsman bungalows vs. brick ranches) affect remodeling costs?",
  answer: "Most historic 1920s craftsman bungalows in Oakhurst, Winnona Park, and MAK Historic District feature original 3-inch or 4-inch cast-iron soil stacks, galvanized supply pipes with severe mineral calcification, and 2x8 floor joists that have deflected over a century. Installing a 350 lb cast-iron soaking tub or large-format floor tile requires opening ceilings from below to sister joists and establish level subfloors (deflection rating L/720 or better). In 1950s mid-century brick ranches around Clairemont and Medlock, bathroom footprints are often constrained between masonry load-bearing walls with low crawlspace clearances, requiring specialized plumbing trenches and engineered LVL beams if walls are moved to expand the owner suite."
}, {
  id: "faq-2",
  question: "What is the difference between City of Decatur and unincorporated DeKalb County permitting?",
  answer: "Jurisdiction dictates permit review times, tree protection requirements, and inspection cadences. Projects within the City of Decatur municipal limits require submittals to the Decatur Design Review Board (if located within local historic districts like MAK or Old Decatur) and trade reviews via Decatur Community & Economic Development. City of Decatur permits typically issue within 10 to 18 business days, and inspectors perform separate rough-in inspections for plumbing, electrical, and framing. In unincorporated DeKalb County (e.g., North Decatur or Medlock Park), permits route through DeKalb County Central Permitting on Memorial Drive, which often takes 3 to 5 weeks for initial trade review. We manage 100% of plan submissions, engineering calculations, and on-site inspection meetings for both jurisdictions."
}, {
  id: "faq-3",
  question: "What specific factors extend or shorten a Decatur bathroom renovation timeline?",
  answer: "Four critical variables control project duration: (1) Custom frameless glass enclosures: tempered glass cannot be fabricated until all wall and curb tile is completely grouted and cured, creating a 2 to 3 week templating and delivery window. (2) Material selections: in-stock artisan porcelain arrives in days, whereas handcrafted Moroccan zellige or custom Italian marble slabs require 4 to 8 weeks of procurement lead time. (3) Plumbing stack relocations: shifting a toilet waste pipe more than 3 feet or moving main vent stacks across exterior walls adds 4 to 6 working days of rough-in plumbing and structural blocking. (4) Historic District approvals: exterior modifications affecting windows or venting in designated historic districts require early commission review before demolition begins."
}, {
  id: "faq-4",
  question: "Can we remain living in our home during a full master bathroom remodel?",
  answer: "Yes. Over 85% of our clients stay home during construction. We seal all work zone doorways with floor-to-ceiling zippered plastic barriers, place temporary adhesive floor protection from the entrance to the bathroom, and run commercial HEPA negative-air scrubbers to trap fine silica and drywall dust. We also verify that at least one secondary full or half bath remains 100% operational with hot water and power throughout the entire construction schedule."
}, {
  id: "faq-5",
  question: "How does Decatur Heritage Renovations handle unexpected discovery items behind walls?",
  answer: "Before starting demolition, we conduct non-destructive moisture readings and crawlspace framing evaluations. However, historic renovations can occasionally reveal unvented S-traps, active knob-and-tube wiring concealed in dead wall cavities, or historic termite damage in sill plates. If an issue is uncovered, work in that micro-area halts immediately while we photograph the condition, prepare an itemized fixed-price change order with direct trade pricing, and review options with you before performing any corrective framing or plumbing."
}, {
  id: "faq-6",
  question: "Why do you emphasize complete continuous waterproofing over standard backer board?",
  answer: "Traditional greenboard or cement board alone is porous and allows moisture vapor to penetrate wall cavities over time, causing hidden stud rot and mildew in older framing. We install uncoupling membranes and continuous bonded waterproof sheet systems (such as Schluter-KERDI or hydro-barrier membranes) with pre-sloped shower pans, waterproof niche inserts, and waterproof pipe seals around all rough-in valves, backed by our comprehensive structural warranty."
}];
export default function CostTimeline() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="cost-timeline" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-border bg-secondary/70 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-foreground">
            Decatur Investment &amp; Schedules
          </Badge>

          <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Bathroom remodeling costs, real data &amp; timelines
          </h2>

          <p className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
            Every Decatur residence carries unique structural character—from century-old heart-pine framing in Oakhurst bungalows to mid-century crawlspaces in Winnona Park. Below is our transparent pricing guide detailing scope, material tiers, mechanical updates, and verified construction schedules.
          </p>
        </div>

        {/* 3-Column Pricing Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {tierList.map((tier, index) => <motion.div key={tier.id} variants={itemVariants} data-index={index} className="flex">
              <Card className={`relative flex h-full w-full flex-col justify-between overflow-hidden rounded-xl border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-border hover:shadow-md sm:p-8 ${tier.popular ? "border-foreground/30 shadow-md ring-1 ring-border" : "border-border"}`}>
                {tier.popular && <div className="absolute top-0 right-0 rounded-bl-lg bg-secondary px-3 py-1 text-xs font-semibold tracking-wide text-foreground">
                    Most Requested
                  </div>}

                <div>
                  <CardHeader className="p-0">
                    <CardTitle className="font-serif text-2xl font-semibold text-card-foreground">
                      {tier.name}
                    </CardTitle>
                    <p className="mt-1 font-sans text-sm text-muted-foreground">
                      {tier.subtitle}
                    </p>
                  </CardHeader>

                  {/* Copper Hairline Divider */}
                  <div className="my-5 h-0.5 w-12 bg-chart-1" />

                  {/* Price and Schedule Block */}
                  <div className="rounded-lg bg-secondary/50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Typical Investment
                    </div>
                    <div className="mt-1 font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                      {tier.investment}
                    </div>

                    <div className="mt-3 flex items-center gap-2 border-t border-border/60 pt-3 text-xs font-medium text-foreground">
                      <Clock className="size-4 text-chart-1" />
                      <span>Estimated Timeline: <strong className="font-semibold">{tier.timeline}</strong></span>
                    </div>
                  </div>

                  {/* Tier Description */}
                  <p className="mt-4 font-sans text-xs leading-relaxed text-muted-foreground">
                    {tier.description}
                  </p>

                  {/* Scope Checklist */}
                  <CardContent className="mt-5 p-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Included Scope Highlights
                    </div>
                    <ul className="mt-3 space-y-2.5 text-sm text-foreground/90">
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-chart-1" />
                        <span className="leading-snug">{tier.scope1}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-chart-1" />
                        <span className="leading-snug">{tier.scope2}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-chart-1" />
                        <span className="leading-snug">{tier.scope3}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-chart-1" />
                        <span className="leading-snug">{tier.scope4}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-chart-1" />
                        <span className="leading-snug">{tier.scope5}</span>
                      </li>
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="mt-8 p-0 pt-4">
                  <Button asChild variant={tier.popular ? "default" : "outline"} className={`w-full font-medium ${tier.popular ? "bg-foreground text-background hover:bg-foreground/90" : "border-border bg-card text-foreground hover:bg-secondary hover:text-foreground"}`}>
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <span>Request detailed estimate</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Atlanta Remodeling Cost vs Value Table */}
        <div className="mt-12 rounded-xl border border-border bg-card/85 p-6 backdrop-blur-md shadow-sm sm:p-8">
          <div className="flex flex-col items-start justify-between gap-4 border-b border-border/80 pb-5 md:flex-row md:items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-chart-1">
                <TrendingUp className="size-4" />
                <span>Regional Market Benchmark</span>
              </div>
              <h3 className="mt-1 font-serif text-xl font-semibold text-foreground sm:text-2xl">
                Atlanta remodeling cost vs value (2025)
              </h3>
            </div>
            <span className="inline-flex items-center rounded-md border border-border bg-secondary/60 px-3 py-1 text-xs font-medium text-muted-foreground">
              Metro Atlanta Residential Data
            </span>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                Atlanta remodeling cost vs value (2025)
              </caption>
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <th scope="col" className="px-4 py-3 sm:px-6">Project</th>
                  <th scope="col" className="px-4 py-3 text-right sm:px-6">Average Cost</th>
                  <th scope="col" className="px-4 py-3 text-right sm:px-6">Resale Value Added</th>
                  <th scope="col" className="px-4 py-3 text-right sm:px-6">% Cost Recouped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-sans">
                <tr className="transition-colors hover:bg-secondary/20">
                  <th scope="row" className="px-4 py-4 font-medium text-foreground sm:px-6">
                    Bath remodel (midrange)
                  </th>
                  <td className="px-4 py-4 text-right font-medium text-foreground/90 sm:px-6">
                    $25,304
                  </td>
                  <td className="px-4 py-4 text-right font-medium text-foreground/90 sm:px-6">
                    $19,595
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-chart-1 sm:px-6">
                    77.4%
                  </td>
                </tr>
                <tr className="transition-colors hover:bg-secondary/20">
                  <th scope="row" className="px-4 py-4 font-medium text-foreground sm:px-6">
                    Bath remodel (upscale)
                  </th>
                  <td className="px-4 py-4 text-right font-medium text-foreground/90 sm:px-6">
                    $79,717
                  </td>
                  <td className="px-4 py-4 text-right font-medium text-foreground/90 sm:px-6">
                    $36,128
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-chart-1 sm:px-6">
                    45.3%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col items-start justify-between gap-2 border-t border-border/60 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center">
            <p className="italic">
              National survey averages for the Atlanta market, not quotes.
            </p>
            <p className="font-medium text-foreground/80">
              Source: Remodeling Magazine / JLC Cost vs Value Report 2025, Atlanta, GA metro.
            </p>
          </div>
        </div>

        {/* Decatur Renovation Assurance Banner */}
        <div className="mt-10 rounded-xl border border-border bg-secondary/40 p-6 sm:p-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="flex items-start gap-4">
              <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-card text-chart-1 shadow-xs">
                <ShieldCheck className="size-6" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold text-foreground">
                  Written project scopes, structural guarantees, and licensed trade oversight
                </h3>
                <p className="mt-1 max-w-2xl font-sans text-sm leading-relaxed text-muted-foreground">
                  Every Decatur Heritage Renovations project includes detailed line-item specifications, engineered framing plans for subfloor leveling, bonded continuous waterproof warranties, and full compliance with City of Decatur and DeKalb County building codes.
                </p>
              </div>
            </div>

            <Button asChild variant="secondary" className="shrink-0 border border-border bg-card font-semibold text-foreground hover:bg-muted">
              <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2">
                <FileCheck2 className="size-4 text-chart-1" />
                <span>View our 5-step process</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* FAQ / Cost Factors Section */}
        <div className="mt-16 sm:mt-20">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <HelpCircle className="size-4 text-chart-1" />
              <span>Decatur Construction &amp; Permitting FAQs</span>
            </div>
            <h3 className="mt-2 font-serif text-2xl font-semibold text-foreground sm:text-3xl">
              Frequently asked cost, timeline &amp; permitting questions
            </h3>
            <p className="mt-3 font-sans text-sm text-muted-foreground sm:text-base">
              Detailed answers addressing older home plumbing challenges, municipal review processes, and practical project scheduling across the Decatur community.
            </p>
          </div>

          <div className="mx-auto mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                  <AccordionItem value={faq.id} className="rounded-lg border border-border bg-card px-5 py-1 shadow-2xs">
                    <AccordionTrigger className="py-4 text-left font-serif text-base font-semibold text-card-foreground hover:no-underline hover:text-chart-1">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-sans text-sm leading-relaxed text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}